import type { BreadcrumbProps } from '@blueprintjs/core'
import { Popover } from '../components/ui/popover'
import { computed, makeAutoObservable } from 'mobx'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ActionBar } from '../components/ActionBar'
import { AdGroupsTable } from '../components/ad_groups/AdGroupsTable'
import { AdTypeBadge } from '../components/badge/AdTypeBadge'
import { TargetingTypeSmall } from '../components/badge/TargetingTypeSmall'
import { Actions } from '../components/details/UpdateActions'
import { Layout } from '../components/Layout'
import { SearchTermsTable2 } from '../components/SearchTermsTable2'
import type { ExpandedPerformanceMetrics } from '../components/table2/ad_grid_columns'
import { TableStore } from '../components/table2/table_store'
import { TargetACOS } from '../components/TargetACOS'
import { TabbedCards } from '../components/ui/TabbedCards'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { useLatestRequestEffect } from '../hooks/useLatestRequestEffect'
import { useScreenshotMode } from '../hooks/useScreenshotMode'
import { useUser } from '../hooks/useUser'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../stores/user_store'
import type { AdGroup } from '../types/ad_group'
import { axiosPatch } from '../util/api'
import { adManagerPerformanceGet } from '../util/adManagerPerformanceApi'
import { cents } from '../util/formatters'
import { OverviewCharts } from './dashboard-overview-charts'

// Map bidding strategy values to user-friendly labels
const BIDDING_STRATEGY_LABELS: Record<string, string> = {
    NONE: 'Fixed bids',
    MANUAL: 'Fixed bids',
    SALES: 'Dynamic bids - up and down',
    SALES_DOWN_ONLY: 'Dynamic bids - down only',
    SALES_UP_AND_DOWN: 'Dynamic bids - up and down',
}

export const formatBiddingStrategy = (strategy: string | null): string => {
    if (!strategy) return 'Unknown'
    return BIDDING_STRATEGY_LABELS[strategy] || strategy
}

// Bid adjustments helper
interface BidAdjustments {
    bid_adjustment_top_of_search: number | null
    bid_adjustment_rest_of_search: number | null
    bid_adjustment_home_page: number | null
    bid_adjustment_product_page: number | null
}

const isSet = (value: number | null | undefined): value is number =>
    value !== null && value !== undefined

const formatAdjustment = (value: number | null | undefined): string =>
    isSet(value) ? `${value}%` : '-'

const BidAdjustmentsPopover = ({ campaign }: { campaign: BidAdjustments }) => {
    const adjustments = [
        { label: 'Top of search', value: campaign.bid_adjustment_top_of_search },
        { label: 'Rest of search', value: campaign.bid_adjustment_rest_of_search },
        { label: 'Home page', value: campaign.bid_adjustment_home_page },
        { label: 'Product page', value: campaign.bid_adjustment_product_page },
    ]

    const count = adjustments.filter((adj) => isSet(adj.value)).length

    const content = (
        <div className="p-2">
            {adjustments.map((adj) => (
                <div key={adj.label} className="flex justify-between gap-4 py-1">
                    <span>{adj.label}</span>
                    <span>{formatAdjustment(adj.value)}</span>
                </div>
            ))}
        </div>
    )

    return (
        <Popover content={content} placement="bottom" interactionKind="hover">
            <span className="cursor-pointer">Bid adjustments ({count})</span>
        </Popover>
    )
}

interface SubStores {
    adGroups: TableStore<AdGroup & ExpandedPerformanceMetrics>
    searchTerms: TableStore<any>
}

class DetailStore {
    activeTab: string = ''
    subStores: SubStores

    constructor(subStores: SubStores) {
        this.subStores = subStores
        makeAutoObservable(this)
    }

    setActiveTab(tab: string) {
        this.activeTab = tab
    }

    get dates(): { from: Date; to: Date } {
        switch (this.activeTab) {
            case 'ad_groups':
                return {
                    from: this.subStores.adGroups.params.from,
                    to: this.subStores.adGroups.params.to,
                }
            case 'search_terms':
                return {
                    from: this.subStores.searchTerms.params.from,
                    to: this.subStores.searchTerms.params.to,
                }
            default:
                console.warn('unknown tab', this.activeTab)
                return {
                    // 30 days ago
                    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    to: new Date(),
                }
        }
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get from() {
        return this.dates.from
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get to() {
        return this.dates.to
    }
}

/**
 * Campaign Detail Page (v5 - Core/PostgreSQL)
 *
 * Uses v5 API endpoints backed by Core/PostgreSQL for campaign data.
 * This is the only campaign detail implementation.
 */
export const CampaignDetailV5 = observer(() => {
    const { campaign_id } = useParams()
    const [campaign, setCampaign] = useState(null)
    const { profile, searchParams } = useUser()
    const nameClassName = classNames({ blur: useScreenshotMode() })
    const { adGroupsTableStore, searchTermsTableStore, detailStore } = useMemo(() => {
        const adGroups = new TableStore<AdGroup & ExpandedPerformanceMetrics>(
            userStore,
            `ad_groups_for_campaign_v5_${campaign_id}`,
        )
        const searchTerms = new TableStore<any>(
            userStore,
            `search_terms_for_campaign_v5_${campaign_id}`,
        )

        return {
            adGroupsTableStore: adGroups,
            searchTermsTableStore: searchTerms,
            detailStore: new DetailStore({
                adGroups,
                searchTerms,
            }),
        }
    }, [campaign_id])
    const savedFiltersStore = useSavedFiltersStore()

    const reloadMetadata = useLatestRequestEffect(
        () =>
            profile
                ? adManagerPerformanceGet(`api/v5/campaigns/${campaign_id}`, {}, true).then((res) => res.data)
                : undefined,
        setCampaign,
        [profile, campaign_id, searchParams],
        undefined,
        () => setCampaign(null),
    )

    // Updates still go through Laravel API
    const update = (payload) => axiosPatch(`campaigns/${campaign_id}`, payload)

    // Load ad groups from the v5 Core API.
    const loadAdGroups = (params: any) => {
        if (!profile) {
            return Promise.resolve()
        }

        const p = {
            ...params,
            filter: {
                ...params.filter,
                campaign_id: campaign_id,
            },
        }

        return adManagerPerformanceGet('api/v5/ad-groups', p, true)
    }

    // Load search terms from the v5 Core API.
    const loadSearchTerms = (params) => {
        if (!profile) {
            return Promise.resolve()
        }

        const p = {
            ...params,
            filter: {
                ...params.filter,
                campaign_id,
            },
        }

        return adManagerPerformanceGet('api/v5/search-terms', p, true)
            .then((res) => {
                // This is a custom mutator. We are basically taking the performance models
                // and flattening them out, so we have a single array with the top level
                // search terms and their children keywords or targets underneath
                const output = []

                res.data.data.forEach((searchTerm) => {
                    output.push({
                        ...searchTerm,
                        type_of: 'search_term',
                        original_type: searchTerm.type_of,
                    })

                    searchTerm?.performances?.forEach((item) => {
                        item.select_key = `${item.type_of}_${item.id}`
                        item.type_of = 'ad_group'
                        output.push(item)
                    })
                })
                res.data.data = output

                return res
            })
            .catch((err) => {
                console.error(err)
            })
    }

    const adGroupsTableHooks = useTableHooksFromStore(
        loadAdGroups,
        {},
        false,
        adGroupsTableStore,
    )
    const searchTermsTableHooks = useTableHooksFromStore(
        loadSearchTerms,
        {},
        false,
        searchTermsTableStore,
    )

    const actions = (
        <ActionBar className="flex items-center justify-between pl-2 pr-4">
            <div className="flex items-center gap-6">
                {campaign && (
                    <Fragment>
                        <AdTypeBadge {...campaign} />
                        <TargetingTypeSmall
                            targeting_type={campaign.targeting_type}
                            state={campaign.state}
                        />
                        <div>
                            Bidding strategy: {formatBiddingStrategy(campaign.bidding_strategy)}
                        </div>
                        <div>Daily budget {cents(campaign.daily_budget)}</div>

                        <div className="flex items-center">
                            <span className="mr-1">Target ACoS</span>
                            <TargetACOS
                                target="campaign"
                                product={campaign}
                                mutateItems={(cb) => setCampaign((prev) => cb([prev])[0])}
                            />
                        </div>

                        <BidAdjustmentsPopover campaign={campaign} />
                    </Fragment>
                )}
            </div>
            <Actions
                name="Campaign"
                showTacos
                showBudget
                showSmartBid={false}
                showBid={false}
                update={update}
                reloadParent={reloadMetadata}
                data={campaign}
            />
        </ActionBar>
    )

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }, { text: 'Campaign:' }]

    return (
        <Layout
            primary={<span className={nameClassName}>{campaign?.name}</span>}
            breadcrumbs={breadcrumbs}
            actions={actions}
        >
            <OverviewCharts
                params={{
                    type: 'campaigns',
                    filter: { campaign_id: campaign_id },
                    from: detailStore.from,
                    to: detailStore.to,
                    previous: true,
                }}
            />

            <TabbedCards
                tabs={['Search Terms', 'Ad Groups']}
                onChange={(tab) => detailStore.setActiveTab(tab)}
            >
                <SearchTermsTable2
                    nested={true}
                    tableHooks={searchTermsTableHooks}
                    store={searchTermsTableStore}
                    savedFiltersStore={savedFiltersStore}
                />
                <AdGroupsTable
                    nested={true}
                    tableHooks={adGroupsTableHooks}
                    store={adGroupsTableStore}
                    savedFiltersStore={savedFiltersStore}
                    showCharts={false}
                />
            </TabbedCards>
        </Layout>
    )
})
